import { useState } from 'react';
import TecladoNumero from './TecladoNumero';
import { formatarCentavos } from '@/pages/loja/utils';

interface Props {
  total: number;
  confirmando: boolean;
  aoConfirmar: (recebido: number) => void;
  aoFechar: () => void;
}

// Folha ÚNICA do dinheiro: total, atalhos (Exato · 20 · 50 · 100), teclado,
// troco ao vivo e o botão "Confirmar · troco R$ X". Nada de "já recebi?".
export default function FolhaDinheiro({ total, confirmando, aoConfirmar, aoFechar }: Props) {
  const [centavos, setCentavos] = useState('');

  const recebido = centavos ? parseInt(centavos, 10) : 0;
  const troco = recebido - total;
  const falta = recebido < total;

  function digitar(d: string) {
    setCentavos((t) => (t + d).replace(/^0+/, '').slice(0, 7));
  }

  function apagar() {
    setCentavos((t) => t.slice(0, -1));
  }

  const atalhos: { rotulo: string; valor: number }[] = [
    { rotulo: 'Exato', valor: total },
    { rotulo: '20', valor: 2000 },
    { rotulo: '50', valor: 5000 },
    { rotulo: '100', valor: 10000 },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-background-50">
      <div className="flex items-center justify-between border-b border-secondary-200 px-4 py-3">
        <button
          type="button"
          onClick={aoFechar}
          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-sm font-medium text-secondary-600 transition-colors hover:text-foreground-900"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-arrow-left-line" />
          </span>
          Voltar
        </button>
        <h2 className="text-base font-bold text-foreground-950">Dinheiro</h2>
        <span className="w-14" />
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4">
        <div className="rounded-2xl bg-white p-4 text-center">
          <p className="text-sm font-semibold uppercase tracking-wide text-secondary-500">Total</p>
          <p className="text-4xl font-extrabold tabular-nums text-foreground-950">
            {formatarCentavos(total)}
          </p>
        </div>

        <p className="mt-4 text-sm font-semibold text-foreground-700">Quanto recebeu?</p>

        <div className="mt-2 grid grid-cols-4 gap-2">
          {atalhos.map((a) => {
            const ativo = recebido === a.valor && a.valor > 0;
            return (
              <button
                key={a.rotulo}
                type="button"
                onClick={() => setCentavos(String(a.valor))}
                className={`cursor-pointer whitespace-nowrap rounded-xl border-2 py-2.5 text-base font-bold transition-colors ${
                  ativo
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-secondary-200 bg-white text-foreground-800 hover:border-primary-300'
                }`}
              >
                {a.rotulo}
              </button>
            );
          })}
        </div>

        <div className="mt-3 rounded-2xl bg-white p-4 text-center">
          <p className="text-3xl font-extrabold tabular-nums text-foreground-950">
            {formatarCentavos(recebido)}
          </p>
        </div>

        <div
          className={`mt-3 flex items-center justify-center rounded-2xl px-4 py-3 ${
            recebido === 0
              ? 'bg-background-100'
              : falta
                ? 'bg-danger-50'
                : 'bg-success-50'
          }`}
        >
          {recebido === 0 ? (
            <p className="text-sm font-medium text-secondary-500">Digite o valor recebido.</p>
          ) : falta ? (
            <p className="text-base font-bold text-danger-600">
              Falta {formatarCentavos(total - recebido)}
            </p>
          ) : (
            <p className="text-center">
              <span className="mr-2 text-sm font-medium text-secondary-500">Troco</span>
              <span className="text-3xl font-extrabold tabular-nums text-success-600">
                {formatarCentavos(troco)}
              </span>
            </p>
          )}
        </div>

        <div className="mt-4">
          <TecladoNumero
            aoDigitar={digitar}
            aoApagar={apagar}
            aoOk={() => aoConfirmar(recebido)}
            rotuloOk="OK"
            okDesabilitado={falta || confirmando}
          />
        </div>
      </div>

      <div
        className="border-t border-secondary-200 bg-white px-5 py-4"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <button
          type="button"
          disabled={falta || confirmando}
          onClick={() => aoConfirmar(recebido)}
          className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-success-600 px-6 py-4 text-lg font-extrabold text-white transition-colors hover:bg-success-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {confirmando ? (
            <>
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-loader-4-line animate-spin" />
              </span>
              Registrando…
            </>
          ) : (
            `Confirmar · troco ${formatarCentavos(Math.max(0, troco))}`
          )}
        </button>
      </div>
    </div>
  );
}